import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import { setCredentials, logout } from '../store/slices/authSlice';
import { useGetCurrentUserQuery } from '../store/api/userApi';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { token, isAuthenticated, user } = useSelector((state: any) => state.auth);
  
  const { data: userData, isLoading, error } = useGetCurrentUserQuery(undefined, {
    skip: !token,
  });

  useEffect(() => {
    if (userData && token) {
      dispatch(setCredentials({ user: userData, token }));
    }
  }, [userData, token, dispatch]);

  // If no token or not authenticated, redirect to login
  if (!token || !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If we have a token but there's an auth error, redirect to login
  if (error) {
    const errorStatus = (error as any)?.status;
    const isAuthError = errorStatus === 401 || errorStatus === 'FETCH_ERROR';
    
    if (isAuthError) {
      dispatch(logout());
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    // For other errors, continue rendering (don't logout user)
  }

  // If we have a token but no user data yet, show loading
  if (token && !user && isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};
